// services/frontend/src/components/atomic/PhotoReferenceThumbnail.tsx
/**
 * PhotoReferenceThumbnail Component
 * Feature: 036-markdown-critique-comments
 * Task: T025
 *
 * Renders inline photo thumbnails for @-mention references in markdown content.
 * Displays a small thumbnail (80x80px max) with hover preview (400px max) using Radix UI Tooltip.
 *
 * Usage:
 * <PhotoReferenceThumbnail objectId="ph1a" />
 */

import React from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { decodeGlobalId } from '@206mp/shared';
import { usePhotoByIdGraphQL } from '../../graphql/hooks/usePhotosGraphQL';
import './PhotoReferenceThumbnail.css';

interface PhotoReferenceThumbnailProps {
  objectId: string; // e.g., "ph1a"
}

export const PhotoReferenceThumbnail: React.FC<PhotoReferenceThumbnailProps> = ({ objectId }) => {
  // Decode global ID to numeric ID
  const decoded = decodeGlobalId(objectId);

  // Handle decoding errors
  if ('error' in decoded) {
    return (
      <span className="mention-error" title={`Invalid photo reference: ${decoded.error}`}>
        @{objectId}
      </span>
    );
  }

  // Verify this is a photo object
  if (decoded.type !== 'photos') {
    return (
      <span className="mention-error" title={`Expected photo reference, got ${decoded.type}`}>
        @{objectId}
      </span>
    );
  }

  // Use the global ID directly to fetch photo data
  const { data: photo, isLoading, error } = usePhotoByIdGraphQL(objectId);

  // Loading state
  if (isLoading) {
    return <span className="mention-loading" title="Loading photo...">@{objectId}</span>;
  }

  // Error states
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load photo';
    return (
      <span className="mention-error" title={errorMessage}>
        @{objectId}
      </span>
    );
  }

  if (!photo) {
    return (
      <span className="mention-error" title="Photo not found">
        @{objectId}
      </span>
    );
  }

  // Helper to get best URL (publicUrl preferred, storageUrl fallback) - Feature 077
  type VariantWithUrls = { variantType: string; storageUrl: string; publicUrl?: string | null };
  const getBestUrl = (variant: VariantWithUrls | undefined) => variant?.publicUrl || variant?.storageUrl;

  // Get URLs for thumbnail and medium resolution from variants array
  const thumbnailUrl = getBestUrl(photo.variants?.find((v: VariantWithUrls) => v.variantType === 'thumbnail'))
    || getBestUrl(photo.variants?.find((v: VariantWithUrls) => v.variantType === 'small'))
    || getBestUrl(photo.variants?.[0]);
  const mediumUrl = getBestUrl(photo.variants?.find((v: VariantWithUrls) => v.variantType === 'medium'))
    || getBestUrl(photo.variants?.find((v: VariantWithUrls) => v.variantType === 'full'))
    || thumbnailUrl;

  // Successful load - render thumbnail with hover preview
  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="mention-photo-wrapper">
            <img
              src={thumbnailUrl}
              alt={photo.title || 'Referenced photo'}
              className="mention-photo-thumbnail"
              loading="lazy"
            />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="top" className="mention-photo-preview" sideOffset={8}>
            <img
              src={mediumUrl}
              alt={photo.title || 'Referenced photo'}
              className="mention-photo-preview-image"
              loading="lazy"
            />
            {photo.title && (
              <p className="mention-photo-title">{photo.title}</p>
            )}
            <Tooltip.Arrow className="mention-photo-preview-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
